import { useState, useCallback } from "react";
import Button from "../../../form/Button";
import Modal from "../../Modal";
import DynamicNameQuestion from "../../../sections/front/DynamicCheckout/DynamicnameQuestion";
import AddBuyerQuestion from "../../../sections/front/DynamicCheckout/AddBuyerQuestion";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useOrganization } from "../../../../context/OrganizationContext";
import { toast } from "react-toastify";
import {
  dynamicFormDelete,
  formOrdering,
} from "../../../../services/dynamicFormService";
import { useSelector } from "react-redux";
import QuestionItem from "./QuestionItem";
import { lngDetect } from "../../../../helpers/lngDetect";
import { useTranslation } from "react-i18next";

// Constants
const STATIC_QUESTION_LABELS = [
  "first name|last name",
  "first name | last name",
  "full name",
  "email",
  "first name",
  "last name",
];

const DISABLED_FIELD_LABELS = [
  "email",
  "first name | last name",
  "first name|last name",
  "full name",
];

const CheckoutForm = ({
  formTitle,
  sectionData = {},
  form = {},
  globalData = {},
  formFor,
  onAddEditQuestionResponse = () => {},
  onDeleteQuestionResponse = () => {},
  onOrderingResponse = () => {},
  hasSection,
  readOnly = false,
  crudPayload = {},
}) => {
  const { t } = useTranslation();
  // State management
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [addQuestionModalOpen, setAddQuestionModalOpen] = useState({
    isOpen: false,
    data: null,
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState({
    isOpen: false,
    data: null,
  });
  const [nameFormat, setNameFormat] = useState("seperateName");
  const [modalType, setModalType] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedOverIndex, setDraggedOverIndex] = useState(null);

  // Context and hooks
  const { selectedOrganization } = useOrganization();
  const { lang } = useSelector((state) => state?.langProvider);
  const queryClient = useQueryClient();

  // Utility functions
  const isStaticQuestion = useCallback(
    (field) => {
      const fieldLabel =
        lngDetect(field.label?.text)?.toLowerCase()?.trim() ||
        field.label?.toLowerCase()?.trim() ||
        "";

      return STATIC_QUESTION_LABELS.some((staticLabel) => {
        const cleanStaticLabel = staticLabel.toLowerCase().trim();
        // Exact match after case normalization and trimming
        return fieldLabel === cleanStaticLabel;
      });
    },
    [lang]
  );

  const isDisabledField = useCallback(
    (field) => {
      const fieldLabel =
        lngDetect(field.label?.text)?.toLowerCase() ||
        field.label?.toLowerCase() ||
        "";
      return DISABLED_FIELD_LABELS.some((label) => fieldLabel.includes(label));
    },
    [lang]
  );

  const getQuestionType = useCallback(
    (field) => {
      const fieldLabel =
        lngDetect(field.label?.text)?.toLowerCase() ||
        field.label?.toLowerCase() ||
        "";
      if (fieldLabel.includes("name")) return "name";
      if (fieldLabel.includes("email")) return "email";
      return "custom";
    },
    [lang]
  );

  // Process form fields into questions
  const processFormFields = (fields, isGlobal = false) => {
    if (!fields?.length) return [];

    const questions = [];
    const sortedFields = [...(fields || [])].sort(
      (a, b) => (a.ordering || 0) - (b.ordering || 0)
    );

    sortedFields.forEach((field) => {
      const isStatic = isStaticQuestion(field);
      const isDisabled = isDisabledField(field);
      const questionType = getQuestionType(field);

      questions.push({
        ...(isGlobal
          ? {}
          : { formId: form?._id, sectionId: sectionData?._id, id: field._id }),
        type: field.type,
        questionType,
        label: lngDetect(field.label?.text) || "Untitled",
        placeholder: lngDetect(field.placeholder) || "",
        defaultValue: field.defaultValue || false,
        options:
          field.options?.map((opt) => ({
            label: lngDetect(opt.label?.text) || "",
            value: lngDetect(opt.key?.text),
            default: opt.default || false,
          })) || [],
        required: field.required || false,
        editable: !isDisabled,
        deletable: !isStatic && !isDisabled,
        source: isStatic ? "static" : isGlobal ? "global" : "dynamic",
        ordering: field.ordering || 0,
        disabled: isDisabled,
        isGlobal,
      });
    });
    return questions;
  };

  // questions list
  const updatedQuestions = [
    ...processFormFields(globalData?.relatedField, true),
    ...processFormFields(sectionData?.relatedField, false),
  ];

  // Mutations
  const { mutate: deleteQuestion, isPending: isDeleting } = useMutation({
    mutationFn: (id) => dynamicFormDelete({ id, contentType: "field" }),
    onSuccess: (res) => {
      if (res?.status === 200) {
        onDeleteQuestionResponse({ ...res, id: deleteModalOpen?.data?.id });
        toast.success("Question deleted successfully");
        setDeleteModalOpen({ isOpen: false, data: null });
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: () => toast.error("Failed to delete question. Please try again."),
  });

  const handleEditClick = useCallback((item) => {
    const isStatic = item.source === "static";

    if (isStatic) {
      const isNameQuestion = item.questionType === "name";
      setModalType(isNameQuestion ? "name" : "email");
      setModalOpen({
        isOpen: true,
        data: { ...item, isEdit: true },
      });
    } else {
      setAddQuestionModalOpen({
        isOpen: true,
        data: { ...item, isEdit: true, formId: item.formId },
      });
    }
  }, []);

  // Drag and Drop handlers
  const handleDragStart = useCallback((e, item, index) => {
    if (item.disabled) {
      e.preventDefault();
      return;
    }
    setDraggedItem({ item, index });
    e.dataTransfer.effectAllowed = "move";
    e.currentTarget.style.opacity = "0.5";
  }, []);

  const handleDragEnd = useCallback((e) => {
    e.currentTarget.style.opacity = "1";
    setDraggedItem(null);
    setDraggedOverIndex(null);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const handleDragEnter = useCallback((e, index) => {
    e.preventDefault();
    setDraggedOverIndex(index);
  }, []);

  const handleDragLeave = useCallback((e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDraggedOverIndex(null);
    }
  }, []);

  const handleDrop = useCallback(
    async (e, dropIndex) => {
      e.preventDefault();

      if (!draggedItem || draggedItem.index === dropIndex) return;

      const newQuestions = [...updatedQuestions];
      const draggedQuestion = newQuestions[draggedItem.index];

      newQuestions.splice(draggedItem.index, 1);
      newQuestions.splice(dropIndex, 0, draggedQuestion);

      const organizationQuestions = newQuestions.filter((q) => !q.isGlobal);

      const res = await formOrdering({
        ordering_data: organizationQuestions.map((item, i) => ({
          id: item.id,
          ordering: i,
        })),
      });

      if (res?.status === 200) {
        onOrderingResponse(organizationQuestions);
      } else {
        toast.warning(res?.message || res?.error);
      }

      setDraggedItem(null);
      setDraggedOverIndex(null);
    },
    [draggedItem, updatedQuestions, queryClient, selectedOrganization]
  );

  // Modal close handlers
  const closeModalOpen = useCallback(
    () => setModalOpen((prev) => ({ ...prev, isOpen: false })),
    []
  );

  const closeAddQuestionModal = useCallback(
    () => setAddQuestionModalOpen((prev) => ({ ...prev, isOpen: false })),
    []
  );

  const closeDeleteModal = useCallback(
    () => setDeleteModalOpen((prev) => ({ ...prev, isOpen: false })),
    []
  );

  const handleFormResponse = (res) => {
    if (res?.status === 200 || res?.status === 201) {
      onAddEditQuestionResponse(res);
      setAddQuestionModalOpen({ isOpen: false, data: null });
      setModalOpen({ isOpen: false, data: null });

      if (res?.nameFormat) setNameFormat(res.nameFormat);
    } else {
      toast.warning(res?.message || res?.error);
    }
  };

  return (
    <>
      <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 md:px-5 py-3 md:py-5 space-y-4">
        <div className="flex flex-wrap gap-3 md:gap-0 justify-between items-center">
          <h3 className="text-lg md:text-xl font-semibold">{formTitle}</h3>
          {!readOnly && (
            <Button
              buttonLabel={t("Add a checkout question")}
              buttonClasses="rounded-full hover:bg-gray-800 transition h-10 md:h-12 w-full md:w-auto"
              buttonIcon="fa-solid fa-plus"
              iconPosition="left"
              buttonFunction={(e) => {
                setAddQuestionModalOpen({
                  isOpen: true,
                  totalDataCount: updatedQuestions.length,
                  data: {
                    formId: form?._id,
                    sectionId: sectionData?._id,
                  },
                });
              }}
            />
          )}
        </div>

        {/* <div className="text-base text-gray-500">
          Buyer questions are asked once per order, unless configured otherwise
          in the settings for individual question.
        </div> */}

        <div className="space-y-2">
          {updatedQuestions.map((item, index) => (
            <QuestionItem
              key={item.id || `question-${index}`}
              data={item}
              index={index}
              draggedOverIndex={draggedOverIndex}
              handleDragLeave={handleDragLeave}
              handleDragOver={handleDragOver}
              handleDragEnter={handleDragEnter}
              handleDrop={handleDrop}
              handleDragStart={handleDragStart}
              handleDragEnd={handleDragEnd}
              draggedItem={draggedItem}
              onEdit={handleEditClick}
              onDelete={(item) =>
                setDeleteModalOpen({ isOpen: true, data: item })
              }
              readOnly={readOnly}
            />
          ))}
        </div>
      </div>

      {/* Edit Static Question Modal */}
      <Modal
        title={
          modalType === "name" ? "Edit name question" : "Edit email question"
        }
        subtitle={
          modalType === "name"
            ? "The 'Name' question is compulsory and can not be removed from the form."
            : "The 'Email' question is compulsory and can not be removed from the form."
        }
        isOpen={modalOpen?.isOpen}
        setIsOpen={closeModalOpen}
        size="xl3"
      >
        <DynamicNameQuestion
          data={modalOpen?.data}
          close={closeModalOpen}
          nameFormat={nameFormat}
          modalType={modalType}
          getResponse={handleFormResponse}
        />
      </Modal>

      {/* Add/Edit Dynamic Question Modal */}
      <Modal
        title={t(
          addQuestionModalOpen?.data?.isEdit
            ? "Edit checkout question"
            : "Add checkout question"
        )}
        subtitle={
          <div className="text-slate-500 font-medium text-sm">
            {t(
              addQuestionModalOpen?.data?.isEdit
                ? "Edit the existing question that will be asked to customers when they make an order"
                : "Create a new question that will be asked to customers when they make an order"
            )}
            .
          </div>
        }
        isOpen={addQuestionModalOpen?.isOpen}
        setIsOpen={closeAddQuestionModal}
        size="xl3"
      >
        <AddBuyerQuestion
          data={addQuestionModalOpen?.data}
          close={closeAddQuestionModal}
          getResponse={handleFormResponse}
          formFor={formFor}
          hasSection={hasSection}
          crudPayload={crudPayload}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title={`Delete custom question: '${deleteModalOpen?.data?.label}'`}
        isOpen={deleteModalOpen?.isOpen}
        setIsOpen={closeDeleteModal}
        size="xl2"
      >
        <div className="space-y-4">
          <p className="text-gray-700 text-sm">
            Before you delete, please note:
          </p>

          <ul className="list-disc text-sm list-inside space-y-2 text-gray-600">
            <li>
              Once you have deleted a custom question it can not be recovered.
            </li>
            <li>Any answers to this question will still be accessible.</li>
          </ul>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              buttonLabel="Cancel"
              variant="outline"
              buttonClasses="px-6 py-2"
              buttonFunction={closeDeleteModal}
            />
            <Button
              buttonLabel="Delete this custom question"
              variant="primary"
              buttonClasses="bg-red-600 hover:bg-red-700 text-white px-6 py-2"
              buttonFunction={() => deleteQuestion(deleteModalOpen.data.id)}
              loading={isDeleting}
              isDisable={isDeleting}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CheckoutForm;
